"use client";

import { useT } from "../../utilities/TranslatedText";

import styles from "../styles/HeaderShortcuts.module.scss";
import { useState } from "react";
import FontAwesome from "../../utilities/FontAwesome";
import {
  conditionType,
  currentViewType,
} from "../../modals/AdminModal/AdminModalTypes";
import NavigationModel from "../../../utils/NavigationModel";
import { runInAction } from "mobx";
import ApiClient from "../../../backends/ApiClient";
import { observer } from "mobx-react";
import NotificationManager from "../../notifications/NotificationManager";
import classNames from "classnames";
import { addLeadingZeros } from "../../../utils/DataUtilities";
import { Shortcut } from "./Shortcut";
import dynamic from "next/dynamic";
import { useStores } from "../../../stores/useStores";

const DraggableShortcut = dynamic(() => import("./DraggableShortcut"));

export const HeaderShortcuts = observer(() => {
  const t = useT();
  const { userStore, modalStore, organizationStore } = useStores();

  const [, setIsDragging] = useState(false);
  const [reorder, setReorder] = useState(false);

  const containerClassName = classNames({
    [styles.container]: true,
  });

  const reorderClassName = classNames(styles.tile, styles.editorTile, {
    [styles.reorder]: reorder,
  });

  const navigationCreator = async (navigation) => {
    try {
      let index = 0;
      for (
        let i = 0;
        i < organizationStore.currentOrganization.shortcuts.length;
        i++
      ) {
        if (
          organizationStore.currentOrganization.shortcuts[i].parent_id !==
          organizationStore.currentOrganization.id
        ) {
          index = i;
          break;
        }
        index = i + 1;
      }
      const client = await ApiClient();
      const result = await (client.POST(
        "/organizations/{organization_pk}/navigations/",
        {
          params: {
            path: { organization_pk: organizationStore.currentOrganization.id },
          },
          body: navigation,
        },
      ) as any);
      runInAction(() => {
        organizationStore.currentOrganization.shortcuts.splice(
          index,
          0,
          result,
        );
      });
      return result;
    } catch {
      NotificationManager.error("Something went wrong! Please try again.");
    }
  };

  const navigationUpdater = async (navigation) => {
    try {
      const client = await ApiClient();
      const result = await (client.PUT(
        "/organizations/{organization_pk}/navigations/{id}/",
        {
          params: {
            path: {
              organization_pk: organizationStore.currentOrganization.id,
              id: navigation.id,
            },
          },
          body: navigation,
        },
      ) as any);
      runInAction(() => {
        organizationStore.currentOrganization.shortcuts =
          organizationStore.currentOrganization.shortcuts.map((link) => {
            if (link.id === navigation.id) {
              return result;
            }
            return link;
          });
      });
    } catch {
      NotificationManager.error("Something went wrong! Please try again.");
    }
  };

  const deleteNavigation = async (id) => {
    if (confirm("Are you sure you want to do that?")) {
      try {
        const client = await ApiClient();
        await client.DELETE(
          "/organizations/{organization_pk}/navigations/{id}/",
          {
            params: {
              path: {
                organization_pk: organizationStore.currentOrganization.id,
                id: id,
              },
            },
          },
        );
        runInAction(() => {
          organizationStore.currentOrganization.shortcuts =
            organizationStore.currentOrganization.shortcuts.filter(
              (l) => l.id !== id,
            );
        });
      } catch {
        NotificationManager.error("Something went wrong! Please try again.");
      }
    }
  };

  const updateNavigation = (navigation) => {
    modalStore.addModal({
      type: "admin",
      condition: conditionType.EDITING,
      nodeInfo: navigation,
      navigation: organizationStore.currentOrganization.shortcuts.filter(
        (s) => s.parent_id === organizationStore.currentOrganization.id,
      ),
      parent_id: organizationStore.currentOrganization.id,
      navigationUpdater,
    });
  };

  function onHoverCallback(list) {
    runInAction(() => {
      organizationStore.currentOrganization.shortcuts = list;
    });
  }

  async function onDrop() {
    const path = organizationStore.currentOrganization.path.split(".");
    for (
      let i = 0;
      i < organizationStore.currentOrganization.shortcuts.length;
      i++
    ) {
      if (
        organizationStore.currentOrganization.shortcuts[i].parent_id ===
        organizationStore.currentOrganization.id
      ) {
        const newPath = `${path[path.length - 1]}.${addLeadingZeros(i + 1)}`;
        organizationStore.currentOrganization.shortcuts[i].path = newPath;
      }
    }
    try {
      const client = await ApiClient();
      await client.PUT(
        "/organizations/{organization_pk}/navigations/bulk_update/",
        {
          params: {
            path: {
              organization_pk: organizationStore.currentOrganization.id,
            },
          },
          body: organizationStore.currentOrganization.shortcuts.filter(
            (shortcut) =>
              shortcut.parent_id === organizationStore.currentOrganization.id,
          ) as never,
        },
      );
      NotificationManager.success("Shortcuts successfully updated.");
    } catch {
      NotificationManager.error(
        "Oops! Something went wrong. Please try again.",
      );
    }
  }

  return (
    <nav
      aria-label={t("Header shortcuts")}
      className={containerClassName}
      data-testid={"header-shortcuts"}
    >
      <div>
        {userStore.isEditor && (
          <>
            <button
              title={"Add a Shortcut"}
              className={classNames(styles.tile, styles.editorTile)}
              onClick={() => {
                modalStore.addModal({
                  type: "admin",
                  condition: conditionType.ADD_TOPLINK,
                  nodeInfo: new NavigationModel(),
                  navigation:
                    organizationStore.currentOrganization.shortcuts.filter(
                      (s) =>
                        s.parent_id ===
                        organizationStore.currentOrganization.id,
                    ),
                  parent_id: organizationStore.currentOrganization.id,
                  initialView: currentViewType.shortcut,
                  navigationCreator,
                });
              }}
            >
              <FontAwesome
                name={"fa-plus"}
                fixedWidth
                prefix={"fas"}
                ariaHidden
                className={styles.glyph}
              />
            </button>
            <button
              title={"Reorder Shortcuts"}
              className={reorderClassName}
              onClick={() => {
                setReorder((v) => !v);
              }}
            >
              <FontAwesome
                prefix={"fas"}
                fixedWidth
                name={"fa-arrows-up-down"}
                ariaHidden
                className={styles.glyph}
              />
            </button>
          </>
        )}
        {organizationStore.currentOrganization.shortcuts?.length > 0 && (
          <ul>
            {organizationStore.currentOrganization.shortcuts.map(
              (link, index) => {
                if (
                  userStore.isEditor &&
                  link.parent_id === organizationStore.currentOrganization.id
                ) {
                  return (
                    <DraggableShortcut
                      key={link.id}
                      shortcut={link}
                      subNavigation={
                        organizationStore.currentOrganization.shortcuts
                      }
                      onHoverCallback={onHoverCallback}
                      index={index}
                      setIsDragging={setIsDragging}
                      reorder={reorder}
                      updateNavigation={updateNavigation}
                      deleteNavigation={deleteNavigation}
                      className={styles.tile}
                      onDrop={onDrop}
                      canEdit={
                        userStore.isEditor &&
                        link.parent_id ===
                          organizationStore.currentOrganization.id
                      }
                    />
                  );
                } else {
                  return (
                    <Shortcut
                      key={link.id}
                      shortcut={link}
                      updateNavigation={updateNavigation}
                      deleteNavigation={deleteNavigation}
                      className={styles.tile}
                      canEdit={
                        userStore.isEditor &&
                        link.parent_id ===
                          organizationStore.currentOrganization.id
                      }
                    />
                  );
                }
              },
            )}
          </ul>
        )}
      </div>
    </nav>
  );
});
